import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../../../common/arr-icon-button/ArrIconButton';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import ArrTooltip from '../../../../../common/arr-tooltip/ArrTooltip';
import {ReactComponent as AiIconHover} from '../../../../../../assets/images/icon-ai-hover.svg';
import {ReactComponent as DeleteIcon} from '../../../../../../assets/images/delete-chat.svg';
import {RootState} from '../../../../../../redux/store';
import {ISelectItem} from '../../../../../../shared/types';
import {
  initializeJobCreateProtocol,
  resetProtocol,
  setDefineCriteria,
  setFormData,
  setInitialProtocol,
  setProtocolVersionBaseline,
  showToast,
  syncProtocolToFormData,
} from '../../../../../../redux/slice/aiReviewSliceV2';
import {AI_REVIEW_JOB_TYPE} from '../../../constant/AiReviewConstants';
import {ToastType} from '../../../../../../shared/constants';
import {mapProtocolVersionDetailsToFormData} from '../../../protocolIntelligenceV2/utils/protocolVersionPayload';
import {
  useGetProtocolNames,
  useGetProtocolVersionDetails,
  useGetProtocolVersions,
} from '../../../../../../hooks/ai/review/useReview';
import {
  hasMeaningfulProtocolContent,
  isProtocolContentEdited,
} from './protocolContentCompare';
import SelectProtocolModal from './SelectProtocolModal';
import './ProtocolSelection.scss';

export {
  hasMeaningfulProtocolContent,
  isProtocolContentEdited,
} from './protocolContentCompare';

export interface AppliedProtocol {
  protocolId: number;
  protocolName: string;
  versionId: number;
  versionName: string;
}

export type ProtocolSelectionChangeType =
  | 'modified'
  | 'applied'
  | 'cleared'
  | 'reset';

export interface ProtocolSelectionProps {
  onProtocolSelectionChange?: (changeType: ProtocolSelectionChangeType) => void;
}

const ProtocolSelection = ({
  onProtocolSelectionChange,
}: ProtocolSelectionProps = {}) => {
  const dispatch = useDispatch();
  const jobType = useSelector((state: RootState) => state.aiReviewV2.jobType);
  const protocol = useSelector((state: RootState) => state.aiReviewV2.protocol);
  const initialProtocol = useSelector(
    (state: RootState) => state.aiReviewV2.initialProtocol
  );
  const protocolVersionBaseline = useSelector(
    (state: RootState) => state.aiReviewV2.protocolVersionBaseline
  );
  const formData = useSelector((state: RootState) => state.aiReviewV2.formData);
  const storedVersions = useSelector(
    (state: RootState) => state.aiReviewV2.protocolVersions?.versions ?? []
  );
  const artifactTabLabel =
    jobType === AI_REVIEW_JOB_TYPE.issueCoding
      ? t('ai.review.newJobDetail.issueCodes')
      : t('ai.review.newJobDetail.relevanceCriteria');

  const [appliedProtocol, setAppliedProtocol] =
    useState<AppliedProtocol | null>(null);
  const [modalProtocolId, setModalProtocolId] = useState<number | null>(null);
  const [modalVersionId, setModalVersionId] = useState<number | null>(null);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [pendingApplySuccessToast, setPendingApplySuccessToast] = useState(false);

  const {data: protocolNames = []} = useGetProtocolNames();
  const {data: versionsResponse} = useGetProtocolVersions(
    modalProtocolId,
    showSelectModal
  );
  const {data: appliedVersionsResponse} = useGetProtocolVersions(
    protocol.protocolId,
    Boolean(protocol.protocolId)
  );
  const {data: versionDetails} = useGetProtocolVersionDetails(
    modalVersionId,
    showSelectModal
  );

  const hasActiveProtocols = protocolNames.length > 0;
  const resolvedAppliedProtocol = useMemo(() => {
    if (appliedProtocol) {
      return appliedProtocol;
    }
    if (protocol.protocolId == null || protocol.protocolVersionId == null) {
      return null;
    }
    return {
      protocolId: protocol.protocolId,
      protocolName: protocol.protocolName,
      versionId: protocol.protocolVersionId,
      versionName:
        protocol.protocolVersionName ??
        (appliedVersionsResponse?.versions ?? storedVersions).find(
          (version) => version.versionId === protocol.protocolVersionId
        )?.name ??
        '',
    };
  }, [appliedProtocol, appliedVersionsResponse, protocol, storedVersions]);
  const hasManualContent =
    !resolvedAppliedProtocol && hasMeaningfulProtocolContent(protocol);
  const isContentEdited = isProtocolContentEdited(protocol, initialProtocol);
  const isModified =
    Boolean(resolvedAppliedProtocol) &&
    (Boolean(formData?.isProtocolModified) || isContentEdited);

  const notifyProtocolSelectionChange = useCallback(
    (changeType: ProtocolSelectionChangeType) => {
      onProtocolSelectionChange?.(changeType);
    },
    [onProtocolSelectionChange]
  );

  const protocols = useMemo<ISelectItem[]>(
    () =>
      protocolNames.map((item) => ({
        id: item.protocolId,
        name: item.protocolName,
      })),
    [protocolNames]
  );

  const versions = useMemo<ISelectItem[]>(
    () =>
      (versionsResponse?.versions ?? []).map((item) => ({
        id: item.versionId,
        label: item.name,
      })),
    [versionsResponse]
  );

  const closeSelectModal = () => {
    setShowSelectModal(false);
    setModalProtocolId(null);
    setModalVersionId(null);
  };

  const openSelectModal = () => {
    setShowSelectModal(true);
  };

  const handleSelectProtocolClick = () => {
    if (!hasActiveProtocols) {
      return;
    }
    if (resolvedAppliedProtocol || hasManualContent) {
      setShowReplaceModal(true);
      return;
    }
    openSelectModal();
  };

  const handleApply = (selectedProtocol: ISelectItem, version: ISelectItem) => {
    if (!versionDetails) {
      return;
    }

    const protocolId = Number(selectedProtocol.id);
    const versionId = Number(version.id);
    const formData = mapProtocolVersionDetailsToFormData(versionDetails, {
      protocolId,
      protocolVersionId: versionId,
    });

    dispatch(setInitialProtocol(formData));
    dispatch(setProtocolVersionBaseline(formData));
    dispatch(setDefineCriteria('saved'));
    dispatch(
      setFormData({
        caseContext: formData.caseContext,
        relevanceCriteria: formData.relevanceCriteria,
        issueCoding: formData.issueCoding,
        reviewProtocolVersionID: versionId,
        parentProtocolVersionID: undefined,
        isProtocolModified: false,
        isManual: false,
      })
    );
    setAppliedProtocol({
      protocolId,
      protocolName: String(selectedProtocol.name ?? formData.protocolName),
      versionId,
      versionName: String(version.label ?? ''),
    });
    closeSelectModal();
    setPendingApplySuccessToast(true);
    notifyProtocolSelectionChange('applied');
  };

  useEffect(() => {
    if (isModified) {
      notifyProtocolSelectionChange('modified');
    }
  }, [isModified, notifyProtocolSelectionChange]);

  useEffect(() => {
    if (showSelectModal || !pendingApplySuccessToast) {
      return;
    }

    dispatch(
      showToast({
        title: t('common.success'),
        message: t('ai.review.newJobDetail.replaceContentMessage'),
        status: [ToastType.Success],
      })
    );
    setPendingApplySuccessToast(false);
  }, [dispatch, pendingApplySuccessToast, showSelectModal]);

  const handleReplaceConfirm = () => {
    setShowReplaceModal(false);
    openSelectModal();
  };

  const handleClearConfirm = () => {
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(syncProtocolToFormData());
    dispatch(
      setFormData({
        reviewProtocolVersionID: undefined,
        parentProtocolVersionID: undefined,
        isProtocolModified: false,
        isManual: true,
      })
    );
    setAppliedProtocol(null);
    setShowClearModal(false);
    notifyProtocolSelectionChange('cleared');
  };

  const handleResetConfirm = () => {
    const resetBaseline = protocolVersionBaseline ?? initialProtocol;
    // A modified job is backed by a saved-as protocol. Reset restores the
    // original parent protocol, rather than retaining that saved-as ID.
    const resetProtocolId = formData.isProtocolModified
      ? (formData.parentProtocolID ??
        resetBaseline.protocolId ??
        protocol.protocolId)
      : (resetBaseline.protocolId ?? protocol.protocolId);
    const resetVersionId = resetBaseline.protocolVersionId;
    const resetVersionName =
      resetBaseline.protocolVersionName ??
      (appliedVersionsResponse?.versions ?? storedVersions).find(
        (version) => version.versionId === resetVersionId
      )?.name ??
      resolvedAppliedProtocol?.versionName ??
      '';

    dispatch(
      setInitialProtocol({
        ...resetBaseline,
        ...(resetProtocolId != null ? {protocolId: resetProtocolId} : {}),
        ...(resetVersionId != null ? {protocolVersionId: resetVersionId} : {}),
      })
    );
    dispatch(setDefineCriteria('saved'));
    dispatch(
      setFormData({
        caseContext: resetBaseline.caseContext,
        relevanceCriteria: resetBaseline.relevanceCriteria,
        issueCoding: resetBaseline.issueCoding,
        reviewProtocolVersionID: resetBaseline.protocolVersionId,
        parentProtocolVersionID: undefined,
        parentProtocolID: undefined,
        isProtocolModified: false,
        isManual: false,
      })
    );
    if (resetProtocolId != null && resetVersionId != null) {
      setAppliedProtocol({
        protocolId: resetProtocolId,
        protocolName: resetBaseline.protocolName,
        versionId: resetVersionId,
        versionName: resetVersionName,
      });
    }
    setShowResetModal(false);
    notifyProtocolSelectionChange('reset');
  };

  const selectButton = (
    <ArrButton
      variant="primary"
      size="small"
      dataTestId="select-protocol-button"
      onClick={handleSelectProtocolClick}
      disabled={!hasActiveProtocols}
      aria-label={t(
        'ai.review.newJobDetail.protocolSelection.selectProtocolButton'
      )}
    >
      {t('ai.review.newJobDetail.protocolSelection.selectProtocolButton')}
    </ArrButton>
  );

  const bannerActions = resolvedAppliedProtocol ? (
    <Box className="protocol-selection__actions">
      <ArrButton
        variant="primary"
        size="small"
        dataTestId="protocol-selection-change"
        onClick={() => setShowReplaceModal(true)}
        aria-label={t('ai.review.newJobDetail.protocolSelection.change')}
      >
        {t('ai.review.newJobDetail.protocolSelection.change')}
      </ArrButton>
      <ArrIconButton
        icon={<DeleteIcon />}
        className="protocol-selection__clear"
        testId="protocol-selection-clear"
        ariaLabel={t('ai.review.newJobDetail.protocolSelection.clearProtocol')}
        tooltipContent={t(
          'ai.review.newJobDetail.protocolSelection.clearProtocol'
        )}
        tooltipPlacement="top"
        tooltipFlipEnabled={false}
        size="medium"
        shape="none"
        onClick={() => setShowClearModal(true)}
      />
    </Box>
  ) : (
    <Box className="protocol-selection__actions">
      {hasActiveProtocols ? (
        selectButton
      ) : (
        <ArrTooltip
          content={t(
            'ai.review.newJobDetail.protocolSelection.noActiveProtocolsTooltip'
          )}
          placement="top"
          flipEnabled={false}
          focusableWrapper
        >
          {selectButton}
        </ArrTooltip>
      )}
    </Box>
  );

  return (
    <Box className="protocol-selection-host">
      <Box
        className="protocol-selection upload-banner"
        data-testid="protocol-selection"
      >
        <Box className="protocol-selection__content">
          <AiIconHover
            data-testid="ai-icon-hover"
            className="icon-navbar"
            aria-hidden="true"
          />
          {resolvedAppliedProtocol ? (
            <Box
              className="protocol-details"
              data-testid="protocol-selection-applied"
            >
              <Typography
                className="typo-text subhead5_size"
                data-testid="protocol-selection-name"
              >
                {resolvedAppliedProtocol.protocolName}
              </Typography>
              <Typography
                className="typo-text body3_size"
                data-testid="protocol-selection-version"
              >
                {resolvedAppliedProtocol.versionName}
              </Typography>
              {isModified && (
                <Box className="protocol-selection__modified-group">
                  <Typography
                    className="typo-text protocol-selection__modified"
                    data-testid="protocol-selection-modified"
                  >
                    {t('ai.review.jobDetailsTab.modifiedProtocol')}
                  </Typography>
                  <ArrButton
                    variant="text"
                    size="small"
                    dataTestId="protocol-selection-reset"
                    className="protocol-selection__reset"
                    onClick={() => setShowResetModal(true)}
                    aria-label={t('common.reset')}
                  >
                    {t('common.reset')}
                  </ArrButton>
                </Box>
              )}
            </Box>
          ) : (
            <Typography
              className="typo-text body3_size"
              data-testid="protocol-selection-default"
            >
              {t('ai.review.newJobDetail.protocolSelection.bannerHelper')}
            </Typography>
          )}
        </Box>
        {bannerActions}
      </Box>

      <SelectProtocolModal
        open={showSelectModal}
        onClose={closeSelectModal}
        onApply={handleApply}
        protocols={protocols}
        versions={versions}
        onProtocolChange={(selected) =>
          setModalProtocolId(selected?.id != null ? Number(selected.id) : null)
        }
        onVersionChange={(selected) =>
          setModalVersionId(selected?.id != null ? Number(selected.id) : null)
        }
        isDetailsReady={Boolean(versionDetails)}
        description={t(
          'ai.review.newJobDetail.protocolSelection.modalDescription',
          {artifactTab: artifactTabLabel}
        )}
      />

      {showReplaceModal && (
        <ArrModal
          className="protocol-selection-confirm-modal"
          showClose={false}
          showModal={showReplaceModal}
          onClose={() => setShowReplaceModal(false)}
          title={t('ai.review.newJobDetail.protocolSelection.replaceTitle')}
          disableFocusTitle
          dataTestId="replace-protocol-modal"
          buttons={[
            {
              id: 'replace-protocol-cancel',
              label: t('common.cancel'),
              onClick: () => setShowReplaceModal(false),
              variant: 'secondary',
              dataTestId: 'replace-protocol-modal-cancel',
            },
            {
              id: 'replace-protocol-confirm',
              label: t('ai.review.newJobDetail.protocolSelection.replace'),
              onClick: handleReplaceConfirm,
              variant: 'primary',
              dataTestId: 'replace-protocol-modal-confirm',
            },
          ]}
        >
          <Typography
            className="typo-text body2_size text-body-strong"
            id="replace-protocol-modal-description"
            data-testid="replace-protocol-modal-description"
          >
            {t('ai.review.newJobDetail.protocolSelection.replaceDescription', {
              artifactTab: artifactTabLabel,
            })}
          </Typography>
        </ArrModal>
      )}

      {showClearModal && (
        <ArrModal
          className="protocol-selection-confirm-modal"
          showClose={false}
          showModal={showClearModal}
          onClose={() => setShowClearModal(false)}
          title={t('ai.review.protocolIntelligence.clearProtocolTitle')}
          disableFocusTitle
          dataTestId="clear-protocol-modal"
          buttons={[
            {
              id: 'clear-protocol-cancel',
              label: t('common.cancel'),
              onClick: () => setShowClearModal(false),
              variant: 'secondary',
              dataTestId: 'clear-protocol-modal-cancel',
            },
            {
              id: 'clear-protocol-confirm',
              label: t('ai.review.protocolIntelligence.clearContent'),
              onClick: handleClearConfirm,
              variant: 'primary',
              dataTestId: 'clear-protocol-modal-confirm',
            },
          ]}
        >
          <Typography
            className="typo-text body2_size text-body-strong"
            id="clear-protocol-modal-description"
            data-testid="clear-protocol-modal-description"
          >
            {t(
              'ai.review.newJobDetail.protocolSelection.clearProtocolDescription',
              {artifactTab: artifactTabLabel}
            )}
          </Typography>
        </ArrModal>
      )}

      {showResetModal && (
        <ArrModal
          className="protocol-selection-confirm-modal"
          showClose={false}
          showModal={showResetModal}
          onClose={() => setShowResetModal(false)}
          title={t('ai.review.newJobDetail.protocolSelection.resetTitle')}
          disableFocusTitle
          dataTestId="reset-protocol-modal"
          buttons={[
            {
              id: 'reset-protocol-cancel',
              label: t('common.cancel'),
              onClick: () => setShowResetModal(false),
              variant: 'secondary',
              dataTestId: 'reset-protocol-modal-cancel',
            },
            {
              id: 'reset-protocol-confirm',
              label: t('ai.review.newJobDetail.protocolSelection.resetContent'),
              onClick: handleResetConfirm,
              variant: 'primary',
              dataTestId: 'reset-protocol-modal-confirm',
            },
          ]}
        >
          <Typography
            className="typo-text body2_size text-body-strong"
            id="reset-protocol-modal-description"
            data-testid="reset-protocol-modal-description"
          >
            {t('ai.review.newJobDetail.protocolSelection.resetDescription', {
              artifactTab: artifactTabLabel,
            })}
          </Typography>
        </ArrModal>
      )}
    </Box>
  );
};

export default ProtocolSelection;
